import React, { useState } from 'react';
import './WebhookTestResultModal.css';

function WebhookTestResultModal({ result, onClose }) {
    const [copiedSection, setCopiedSection] = useState(null);

    if (!result) return null;

    const webhook = result.webhook || {};
    const isSuccess = result.success;
    const hasError = webhook.error;
    const isBlocked = result.blocked;

    const copyToClipboard = (text, section) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedSection(section);
            setTimeout(() => setCopiedSection(null), 2000);
        });
    };

    const formatJson = (obj) => {
        if (typeof obj === 'string') {
            try {
                return JSON.stringify(JSON.parse(obj), null, 2);
            } catch {
                return obj;
            }
        }
        return JSON.stringify(obj, null, 2);
    };

    const getStatusColor = () => {
        if (hasError) return 'error';
        if (webhook.statusCode >= 200 && webhook.statusCode < 300) return 'success';
        if (webhook.statusCode >= 300 && webhook.statusCode < 400) return 'warning';
        return 'error';
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="webhook-result-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h2>Webhook Test Result</h2>
                        <span className={`test-type-badge ${result.testType}`}>
                            {result.testType === 'ok' ? 'OK Test' : 'NOK Test'}
                        </span>
                    </div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    {/* Blocked Section */}
                    {isBlocked && (
                        <section className="result-section error-section">
                            <div className="section-header">
                                <h3>Webhook Blocked</h3>
                            </div>
                            <div className="section-content">
                                <div className="error-box">
                                    <div className="error-title">Reason:</div>
                                    <div className="error-message">{result.reason}</div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Status Section */}
                    {!isBlocked && (
                        <section className="result-section">
                            <div className="section-header">
                                <h3>Status</h3>
                                <div className={`status-badge ${getStatusColor()}`}>
                                    {hasError ? 'Failed' : isSuccess ? 'Success' : 'Failed'}
                                </div>
                            </div>
                            <div className="section-content">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <label>URL</label>
                                        <div className="url-value">
                                            <code>{webhook.url}</code>
                                        </div>
                                        <button
                                            className="copy-btn-small"
                                            onClick={() => copyToClipboard(webhook.url, 'url')}
                                            title="Copy URL"
                                        >
                                            {copiedSection === 'url' ? 'Copied' : 'Copy'}
                                        </button>
                                    </div>
                                    {!hasError && (
                                    <>
                                        <div className="info-item">
                                            <label>HTTP Status</label>
                                            <div className="info-value">
                                                <span className={`status-code ${getStatusColor()}`}>
                                                    {webhook.statusCode} {webhook.statusText}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <label>Response Time</label>
                                            <div className="info-value">
                                                <span className="duration">
                                                    {webhook.duration}
                                                    {webhook.durationMs && webhook.durationMs < 1000 && (
                                                        <span className="duration-indicator fast"> Fast</span>
                                                    )}
                                                    {webhook.durationMs && webhook.durationMs >= 1000 && webhook.durationMs < 3000 && (
                                                        <span className="duration-indicator moderate"> Moderate</span>
                                                    )}
                                                    {webhook.durationMs && webhook.durationMs >= 3000 && (
                                                        <span className="duration-indicator slow"> Slow</span>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        </section>
                    )}

                    {/* Error Section */}
                    {hasError && !isBlocked && (
                        <section className="result-section error-section">
                            <div className="section-header">
                                <h3>Error Details</h3>
                            </div>
                            <div className="section-content">
                                <div className="error-box">
                                    <div className="error-title">Error Message:</div>
                                    <div className="error-message">{webhook.error}</div>
                                    {webhook.code && (
                                        <div className="error-code">Code: {webhook.code}</div>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Request Headers */}
                    {!isBlocked && webhook.requestHeaders && (
                        <section className="result-section">
                            <div className="section-header">
                                <h3>Request Headers</h3>
                            </div>
                            <div className="section-content">
                                <button
                                    className="copy-btn"
                                    onClick={() => copyToClipboard(formatJson(webhook.requestHeaders), 'requestHeaders')}
                                >
                                    {copiedSection === 'requestHeaders' ? 'Copied' : 'Copy'}
                                </button>
                                <div className="code-block">
                                    <pre>{formatJson(webhook.requestHeaders)}</pre>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Request Body */}
                    {!isBlocked && webhook.requestBody && (
                        <section className="result-section">
                            <div className="section-header">
                                <h3>Request Body</h3>
                            </div>
                            <div className="section-content">
                                <button
                                    className="copy-btn"
                                    onClick={() => copyToClipboard(
                                        typeof webhook.requestBody === 'string'
                                            ? webhook.requestBody
                                            : formatJson(webhook.requestBody),
                                        'requestBody'
                                    )}
                                >
                                    {copiedSection === 'requestBody' ? 'Copied' : 'Copy'}
                                </button>
                                <div className="code-block">
                                    <pre>
                                        {typeof webhook.requestBody === 'string'
                                            ? webhook.requestBody
                                            : formatJson(webhook.requestBody)}
                                    </pre>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Response Headers */}
                    {!isBlocked && !hasError && webhook.headers && (
                        <section className="result-section">
                            <div className="section-header">
                                <h3>Response Headers</h3>
                            </div>
                            <div className="section-content">
                                <button
                                    className="copy-btn"
                                    onClick={() => copyToClipboard(formatJson(webhook.headers), 'responseHeaders')}
                                >
                                    {copiedSection === 'responseHeaders' ? 'Copied' : 'Copy'}
                                </button>
                                <div className="code-block">
                                    <pre>{formatJson(webhook.headers)}</pre>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Response Body */}
                    {!isBlocked && !hasError && webhook.responseData && (
                        <section className="result-section">
                            <div className="section-header">
                                <h3>Response Body</h3>
                            </div>
                            <div className="section-content">
                                <button
                                    className="copy-btn"
                                    onClick={() => copyToClipboard(
                                        typeof webhook.responseData === 'string'
                                            ? webhook.responseData
                                            : formatJson(webhook.responseData),
                                        'response'
                                    )}
                                >
                                    {copiedSection === 'response' ? 'Copied' : 'Copy'}
                                </button>
                                <div className="code-block">
                                    <pre>
                                        {typeof webhook.responseData === 'string'
                                            ? webhook.responseData
                                            : formatJson(webhook.responseData)}
                                    </pre>
                                </div>
                            </div>
                        </section>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="primary-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default WebhookTestResultModal;
